import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, ChartLegend, EmptyState, Skeleton, StackedBarChart, cssVars, type StackSeries } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import type { DashboardOut } from "../shared/api-types";
import type { Formatters } from "../shared/format";
import { axisPicks, toPercent } from "./dashboard-model";

const SERIES_KEYS = { ai: "resolved_by_ai", staff: "handed_to_staff" } as const;

export function ActivitySkeleton() {
  return (
    <section className="grid grid-cols-1 gap-6 xl:grid-cols-3" aria-hidden="true">
      <div className="card card-body space-y-4 xl:col-span-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-56 w-full" />
      </div>
      <div className="card card-body space-y-4">
        <Skeleton className="h-4 w-40" />
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
    </section>
  );
}

function DailyChartCard({ data, format }: { data: DashboardOut; format: Formatters }) {
  const { t } = useTranslation();
  const series: StackSeries[] = [
    { key: SERIES_KEYS.ai, label: t("dashboard.chart.resolvedByAi"), tone: "brand" },
    { key: SERIES_KEYS.staff, label: t("dashboard.chart.handedToStaff"), tone: "ink" },
  ];
  const points = data.daily.map((d) => ({ label: format.shortDate(`${d.date}T12:00:00Z`), values: { [SERIES_KEYS.ai]: d.resolved_by_ai, [SERIES_KEYS.staff]: d.handed_to_staff } }));
  const empty = data.daily.every((d) => d.resolved_by_ai + d.handed_to_staff === 0);
  const range = t("dashboard.chart.range", { start: format.shortDate(`${data.period.start}T12:00:00Z`), end: format.shortDate(`${data.period.end}T12:00:00Z`) });
  return (
    <div className="card xl:col-span-2">
      <div className="card-header">
        <div>
          <h2 className="card-title">{t("dashboard.chart.title")}</h2>
          <div className="mt-0.5 text-xs text-fg-subtle">{range}</div>
        </div>
        <ChartLegend items={series.map((s) => ({ label: s.label, tone: s.tone }))} />
      </div>
      <div className="card-body">
        {empty ? (
          <EmptyState icon="bar-chart" headingLevel={3} title={t("dashboard.chart.emptyTitle")} description={t("dashboard.chart.emptyBody")} className="h-56" />
        ) : (
          <StackedBarChart title={t("dashboard.chart.title")} description={range} series={series} data={points} showLegend={false} axisLabels={axisPicks(points.map((p) => p.label))} formatValue={format.number} />
        )}
      </div>
    </div>
  );
}

function ShareBar({ label, pct }: { label: string; pct: number }) {
  const value = `${String(Math.round(pct))}%`;
  return (
    <li>
      <div className="flex justify-between text-sm">
        <span className="font-semibold">{label}</span>
        <span className="tabular-nums text-fg-muted">{value}</span>
      </div>
      <div className="mt-1.5 h-2 rounded-pill bg-sunken" aria-hidden="true">
        <div className="h-2 w-[var(--geo-width)] rounded-pill bg-brand-500" style={cssVars({ "--geo-width": `${String(Math.min(pct, 100))}%` })} />
      </div>
    </li>
  );
}

function TopicsCard({ data }: { data: DashboardOut }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const languageName = (key: string) => t(`dashboard.topics.languageNames.${key}`, { defaultValue: key });
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">{t("dashboard.topics.title")}</h2>
        <AppLink href={consoleRoutes.analytics(summary.slug)} className="text-xs font-semibold text-primary-ink hover:underline">
          {t("dashboard.topics.details")}
        </AppLink>
      </div>
      <div className="card-body space-y-3.5">
        {data.topics.length === 0 ? (
          <EmptyState icon="tag" headingLevel={3} title={t("dashboard.topics.emptyTitle")} description={t("dashboard.topics.emptyBody")} className="py-6" />
        ) : (
          <ul className="space-y-3.5">
            {data.topics.slice(0, 6).map((topic) => (
              <ShareBar key={topic.key} label={topic.key} pct={toPercent(topic.pct)} />
            ))}
          </ul>
        )}
        {data.languages.length > 0 ? (
          <>
            <div className="divider" />
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-fg-muted">
              <span>{t("dashboard.topics.languages")}</span>
              <span className="flex flex-wrap gap-1.5">
                {data.languages.map((language) => (
                  <Badge key={language.key}>
                    {languageName(language.key)} {String(Math.round(toPercent(language.pct)))}%
                  </Badge>
                ))}
              </span>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/** "Conversations per day" stacked bars + "What people ask about" + language mix. */
export function ActivityCards({ data, format }: { data: DashboardOut; format: Formatters }) {
  return (
    <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <DailyChartCard data={data} format={format} />
      <TopicsCard data={data} />
    </section>
  );
}
